/**
 * Utility functions
 */

import { IFRAME_URLS } from './constants/sdk-constants';
import type { Locale } from './types';

export const SUPPORTED_LOCALES: Locale[] = ['en', 'de', 'es', 'fr', 'it', 'nl', 'pl', 'pt'];

/**
 * Detects the browser's language and returns a supported locale if available.
 * Falls back to 'en' if the browser language is not supported.
 */
export function detectBrowserLocale(): Locale {
  if (typeof window === 'undefined') {
    return 'en';
  }

  try {
    const browserLang =
      navigator.language ||
      (navigator as { userLanguage?: string }).userLanguage ||
      '';

    const primaryLang = browserLang.split('-')[0].toLowerCase();

    if (SUPPORTED_LOCALES.includes(primaryLang as Locale)) {
      return primaryLang as Locale;
    }
  } catch {
    // If detection fails, fall back to default
  }

  return 'en';
}

/**
 * Determines the environment from the API key format
 */
export function getEnvironmentFromApiKey(publicKey: string): 'sandbox' | 'production' | 'development' | 'preprod' {
  if (publicKey.includes('_local_') || publicKey.startsWith('inflow_local_')) {
    return 'sandbox';
  } else if (publicKey.includes('_prod_') && !publicKey.includes('_preprod_')) {
    return 'production';
  } else if (publicKey.includes('_preprod_') || publicKey.startsWith('inflow_preprod_')) {
    return 'preprod';
  } else if (publicKey.includes('_dev_')) {
    return 'development';
  }
  return 'sandbox';
}

/**
 * Gets the iframe URL based on the API key environment
 * This function auto-detects the correct iframe URL from the API key format
 * 
 * @param publicKey - The public API key
 * @returns The iframe URL for the detected environment
 * 
 * @example
 * ```typescript
 * const url = getIframeUrlFromApiKey('inflow_pub_prod_xxx');
 * // Returns: 'https://api.inflowpay.xyz/iframe/checkout'
 * 
 * const url = getIframeUrlFromApiKey('inflow_pub_local_xxx');
 * // Returns: 'http://localhost:3000/iframe/checkout'
 * ```
 */
export function getIframeUrlFromApiKey(publicKey: string): string {
  const environment = getEnvironmentFromApiKey(publicKey);
  
  switch (environment) {
    case 'production':
      return IFRAME_URLS.PRODUCTION;
    case 'preprod':
      return IFRAME_URLS.PREPROD;
    case 'development':
      return IFRAME_URLS.DEVELOPMENT;
    case 'sandbox':
    default:
      return IFRAME_URLS.LOCAL;
  }
}
